import axios, { AxiosRequestConfig, ResponseType } from 'axios';
import Cookies from 'js-cookie';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import _ from 'lodash';

import { decrypt } from '@e-origin/crypto-wasm/browser-loader';
import {
  BatchTypeEnum,
  Declaration,
  DeclarationDocumentsAdditionalFiscalReferenceRole,
  DeclarationHighValueGroupType,
  DeclarationStatus,
  EnvironmentCountries,
  MethodsOfPayment,
  TemplateType,
  X_VISITOR_ID,
} from '@e-origin/shared';

import {
  ADMIN_AUTH_COOKIE_NAME,
  API_ENCODING_KEY,
  AUTH_COOKIE_NAME,
  DECIMAL_PRECISION_FACTOR,
  DEFAULT_DECIMAL_PLACES,
  EURO_DECIMAL_PLACES,
  getAdminBase,
  isAdminBuild,
  KG_DECIMAL_PLACES,
  MILLISECONDS_IN_SECOND,
  PERCENTAGE_FACTOR,
  TOKEN_EXPIRATION_BUFFER_SECONDS,
} from '@constants';
import { FormatValueTypes } from '@enums';
import { refreshToken } from '@hooks/user/useRefreshToken';
import { defaultSettings } from '@hooks/useSettings';
import { getStoredCriteria } from '../criteria.caching';

interface DisableAmendmentFieldProps {
  declaration: Declaration;
  settingsCountry: EnvironmentCountries;
  countries: EnvironmentCountries[];
}

interface IRequest {
  path: string;
  method: any;
  authenticate: boolean;
  dataObject?: any;
  headers?: any;
  fileDownload?: boolean;
  responseType?: ResponseType;
  baseUrl?: string;
}

export interface IResetPasswordFormData {
  newPassword?: string;
  newPasswordConfirmation?: string;
}

export interface ApiError {
  response: {
    data?: {
      message?: string;
    };
    code?: string;
    status?: number;
  };
}

// eslint-disable-next-line max-statements
const request = async (props: IRequest, includeHeaders = false) => {
  const axiosConfigObject: AxiosRequestConfig = {
    url: `${props.baseUrl ?? defaultSettings.apiUrl}/${props.path}`,
    method: props.method,
    data: props.dataObject,
  };
  if (props.fileDownload) {
    axiosConfigObject.responseType = 'blob';
  }

  if (props.responseType) {
    axiosConfigObject.responseType = props.responseType;
  }

  const isAdminPath = isAdminBuild || ((import.meta as any).env.DEV && window.location.pathname.startsWith('/admin'));
  let token = isAdminPath ? Cookies.get(ADMIN_AUTH_COOKIE_NAME) : Cookies.get(AUTH_COOKIE_NAME);

  axiosConfigObject.headers = {};

  const visitorId = Cookies.get(X_VISITOR_ID);
  if (visitorId) {
    axiosConfigObject.headers[X_VISITOR_ID] = visitorId;
  }

  if (props.authenticate) {
    try {
      const decoded = jwtDecode<JwtPayload>(token);

      if (decoded.exp * MILLISECONDS_IN_SECOND < Date.now() + TOKEN_EXPIRATION_BUFFER_SECONDS * MILLISECONDS_IN_SECOND) {
        if (!isAdminPath) {
          await refreshToken();
          token = Cookies.get(AUTH_COOKIE_NAME);
        } else {
          Cookies.set(ADMIN_AUTH_COOKIE_NAME, '');
        }
      }
      axiosConfigObject.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      if (isAdminPath) {
        Cookies.set(ADMIN_AUTH_COOKIE_NAME, '');
      } else {
        Cookies.set(AUTH_COOKIE_NAME, '');
      }
      window.location.href = isAdminPath ? `${getAdminBase()}/auth` : '/auth';
    }
  }
  if (props.headers) {
    axiosConfigObject.headers = { ...axiosConfigObject.headers, ...props.headers };
  }

  const response = await axios(axiosConfigObject);
  const { data, headers } = response;

  const adminSessionExists = Cookies.get(ADMIN_AUTH_COOKIE_NAME);

  // proactively decrypt data coming from API
  //  defaultSettings.isProd && !adminSessionExists && data.data
  if (typeof data.data === 'string') {
    const encryptedBytes = Uint8Array.from(atob(data.data), (c) => c.charCodeAt(0));
    const decryptedString = await decrypt(encryptedBytes, API_ENCODING_KEY);
    const decodedData = JSON.parse(decryptedString);
    if (decodedData) {
      data.data = decodedData;
    }
  }

  const jwtToken = headers['x-jwt-token'];

  if (jwtToken) {
    Cookies.set(AUTH_COOKIE_NAME, jwtToken);
  }
  return !includeHeaders ? data : { data, headers };
};

const validatePassword = (values: IResetPasswordFormData) => {
  const errors: IResetPasswordFormData = {};

  if (!values.newPassword || _.isEmpty(values.newPassword)) {
    errors.newPassword = 'Password required';
  }

  if (!values.newPasswordConfirmation || _.isEmpty(values.newPasswordConfirmation)) {
    errors.newPasswordConfirmation = 'Password confirmation required';
  }

  if (values.newPassword !== values.newPasswordConfirmation) {
    errors.newPasswordConfirmation = 'Passwords must match';
  }

  return errors;
};

const getRoleTypes = () => {
  return [
    {
      label: `${DeclarationDocumentsAdditionalFiscalReferenceRole.IMPORTER} - Importer`,
      value: DeclarationDocumentsAdditionalFiscalReferenceRole.IMPORTER,
    },
    {
      label: `${DeclarationDocumentsAdditionalFiscalReferenceRole.CUSTOMER} - Customer`,
      value: DeclarationDocumentsAdditionalFiscalReferenceRole.CUSTOMER,
    },
    {
      label: `${DeclarationDocumentsAdditionalFiscalReferenceRole.TAX_REPRESENTATIVE} - Tax representative`,
      value: DeclarationDocumentsAdditionalFiscalReferenceRole.TAX_REPRESENTATIVE,
    },
    {
      label: `${DeclarationDocumentsAdditionalFiscalReferenceRole.VENDOR} - Vendor`,
      value: DeclarationDocumentsAdditionalFiscalReferenceRole.VENDOR,
    },
    {
      label: `${DeclarationDocumentsAdditionalFiscalReferenceRole.HOLDER} - Holder`,
      value: DeclarationDocumentsAdditionalFiscalReferenceRole.HOLDER,
    },
    {
      label: `${DeclarationDocumentsAdditionalFiscalReferenceRole.TAXABLE_PERSON} - Taxable reason`,
      value: DeclarationDocumentsAdditionalFiscalReferenceRole.TAXABLE_PERSON,
    },
  ];
};

const getMethodOfPaymentTypes = () => {
  return [
    {
      label: '-',
      value: '',
    },
    {
      label: `${MethodsOfPayment.PAYMENT_IN_CASH} - Payment in cash`,
      value: MethodsOfPayment.PAYMENT_IN_CASH,
    },
    {
      label: `${MethodsOfPayment.PAYMENT_BY_CREDIT_CARD} - Payment by credit card`,
      value: MethodsOfPayment.PAYMENT_BY_CREDIT_CARD,
    },
    {
      label: `${MethodsOfPayment.PAYMENT_BY_CHEQUE} - Payment by cheque`,
      value: MethodsOfPayment.PAYMENT_BY_CHEQUE,
    },
    {
      label: `${MethodsOfPayment.OTHER} - Other (e. g. direct debit to agent's cash account)`,
      value: MethodsOfPayment.OTHER,
    },
    {
      label: `${MethodsOfPayment.DEFERRED_OR_POSTPONED_PAYMENT} - Deferred or postponed payment`,
      value: MethodsOfPayment.DEFERRED_OR_POSTPONED_PAYMENT,
    },
    {
      label: `${MethodsOfPayment.POSTPONED_PAYMENT_VAT_SYSTEM} - Postponed payment — VAT system (Article 211 of Directive 2006/112/EC)`,
      value: MethodsOfPayment.POSTPONED_PAYMENT_VAT_SYSTEM,
    },
    {
      label: `${MethodsOfPayment.ELECTRONIC_CREDIT_TRANSFER} - Electronic credit transfer`,
      value: MethodsOfPayment.ELECTRONIC_CREDIT_TRANSFER,
    },
    {
      // eslint-disable-next-line max-len
      label: `${MethodsOfPayment.PAYMENT_THROUGH_POST_OFFICE_ADMINISTRATION} - Payment through post office administration (postal consignments) or other public sector or government department`,
      value: MethodsOfPayment.PAYMENT_THROUGH_POST_OFFICE_ADMINISTRATION,
    },
    {
      label: `${MethodsOfPayment.EXCISE_CREDIT_OR_REBATE} - Excise credit or rebate`,
      value: MethodsOfPayment.EXCISE_CREDIT_OR_REBATE,
    },
    {
      label: `${MethodsOfPayment.GUARANTEE_LODGED_WITH_INTERVATION_AGENCY} - Guarantee lodged with Intervention Agency`,
      value: MethodsOfPayment.GUARANTEE_LODGED_WITH_INTERVATION_AGENCY,
    },
    {
      label: `${MethodsOfPayment.FROM_AGENTS_CASH_ACCOUNT} - From agent's cash account`,
      value: MethodsOfPayment.FROM_AGENTS_CASH_ACCOUNT,
    },
    {
      label: `${MethodsOfPayment.GUARANTEE_OF_THE_AMOUNT_PAYABLE} - Guarantee of the amount payable`,
      value: MethodsOfPayment.GUARANTEE_OF_THE_AMOUNT_PAYABLE,
    },
    {
      label: `${MethodsOfPayment.INDIVIDUAL_GUARANTEE_ACCOUNT} - Individual guarantee account`,
      value: MethodsOfPayment.INDIVIDUAL_GUARANTEE_ACCOUNT,
    },
    {
      label: `${MethodsOfPayment.FROM_AGENT_GUARANTEE_ACCOUNT} - From agent's guarantee account`,
      value: MethodsOfPayment.FROM_AGENT_GUARANTEE_ACCOUNT,
    },
    {
      label: `${MethodsOfPayment.FROM_AGENT_GUARANTEE_STANDING_AUTHORITY} - From agent's guarantee — standing authority`,
      value: MethodsOfPayment.FROM_AGENT_GUARANTEE_STANDING_AUTHORITY,
    },
    {
      label: `${MethodsOfPayment.FROM_AGENT_GUARANTEE_INDIVIDUAL_AUTHORITY} - From agent's guarantee — individual authority`,
      value: MethodsOfPayment.FROM_AGENT_GUARANTEE_INDIVIDUAL_AUTHORITY,
    },
  ];
};

const STORAGE_KEYS = {
  COOKIES: {
    LIST_BATCHES_CRITERIA: 'list-batches-criteria',
    LIST_TRANSITS_CRITERIA: 'list-transits-criteria',
    LIST_TSD_CRITERIA: 'list-temporary-storages-criteria',
    LIST_DECLARATION_GROUPS_CRITERIA: 'list-declaration-groups-criteria',
    LIST_DECLARATIONS_CRITERIA: 'list-declarations-criteria',
    BATCH_VIEW: 'batchView',
  },
  LOCAL_STORAGE: {
    LIST_BATCHES_COLUMNS: 'list-batches-columns',
    INVITATION_BROKER_TOKEN: 'invitation-broker-token',
  },
};

export const toDecimal = (value: number, decimals = DEFAULT_DECIMAL_PLACES) =>
  Math.round(value * DECIMAL_PRECISION_FACTOR ** decimals) / DECIMAL_PRECISION_FACTOR ** decimals;

const formatValue = (value: number, type: FormatValueTypes) => {
  switch (type) {
    case FormatValueTypes.PERCENTAGE:
      return `${(Math.floor(value * PERCENTAGE_FACTOR) / PERCENTAGE_FACTOR).toFixed(EURO_DECIMAL_PLACES)}`;
    case FormatValueTypes.RATE:
      return `${Math.floor(value * PERCENTAGE_FACTOR).toFixed(EURO_DECIMAL_PLACES)}%`;
    case FormatValueTypes.VALUE: {
      const floatValue = Number((Math.floor(value * PERCENTAGE_FACTOR) / PERCENTAGE_FACTOR).toFixed(EURO_DECIMAL_PLACES));
      return `${floatValue || '-'}€/kg`;
    }
    case FormatValueTypes.KG:
      return `${(Math.floor(value * PERCENTAGE_FACTOR) / PERCENTAGE_FACTOR).toFixed(KG_DECIMAL_PLACES)}kg`;
    case FormatValueTypes.TOTAL_VALUE:
      return `${value.toFixed(KG_DECIMAL_PLACES)}€`;
    default:
      return value.toFixed(KG_DECIMAL_PLACES);
  }
};

export const removeUndefined = (obj) => {
  if (!obj) return {};
  return Object.keys(obj).reduce((acc, key) => (obj[key] !== undefined ? { ...acc, [key]: obj[key] } : acc), {});
};

const templateType = () => {
  const batchView = getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW);
  if (batchView === BatchTypeEnum.HIGH_VALUES) {
    return TemplateType.HIGH_VALUE_H1;
  }
  return TemplateType.LOW_VALUE_H7;
};

const disableAmendmentField = ({ declaration, settingsCountry, countries }: DisableAmendmentFieldProps): boolean => {
  return (
    Boolean(declaration.messageInfo.amended?.LRN) &&
    [DeclarationStatus.NOT_SENT, DeclarationStatus.ANALYSIS].includes(declaration.generalInfo.status) &&
    countries.includes(settingsCountry)
  );
};

const isHighValueDeclaration = (group: string): boolean => {
  return group ? Object.values(DeclarationHighValueGroupType).includes(group as DeclarationHighValueGroupType) : false;
};

const buildDropdownOptions = (options, selected) =>
  options.reduce((acc, { label, value }) => {
    acc[value] = {
      label,
      checked: selected?.includes(value) ?? false,
    };
    return acc;
  }, {});

const VAT_D_PAYMENT_WARNING = 'To use D for VAT payment, please provide E and DPO account in Duties section';

const isVatDeferredPaymentInvalid = (payment: {
  methodOfPaymentVAT?: string | null;
  methodOfPayment?: string | null;
  deferredPayment?: string | null;
}): boolean =>
  payment.methodOfPaymentVAT === MethodsOfPayment.OTHER &&
  !(payment.methodOfPayment === MethodsOfPayment.DEFERRED_OR_POSTPONED_PAYMENT && payment.deferredPayment?.trim()?.length > 0);

export {
  formatValue,
  getMethodOfPaymentTypes,
  getRoleTypes,
  request,
  templateType,
  validatePassword,
  disableAmendmentField,
  isHighValueDeclaration,
  isVatDeferredPaymentInvalid,
  VAT_D_PAYMENT_WARNING,
  buildDropdownOptions,
  STORAGE_KEYS,
};
